// src/Components/Modules/HR/AttendanceSummary.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Divider,
  Avatar,
  InputAdornment,
} from "@mui/material";
import {
  FaSearch,
  FaCalendar,
  FaFilter,
  FaUserClock,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
  FaClock,
  FaSync,
  FaDownload,
  FaUser,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme, status }) => {
  const colors = {
    total: { bg: "#e3f2fd", color: "#1565c0", icon: "#2196f3" },
    present: { bg: "#e8f5e9", color: "#2e7d32", icon: "#4caf50" },
    late: { bg: "#fff3e0", color: "#ef6c00", icon: "#ff9800" },
    absent: { bg: "#ffebee", color: "#c62828", icon: "#f44336" },
  };
  
  const palette = colors[status] || colors.total;
  
  return {
    background: palette.bg,
    borderRadius: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
    },
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const AttendanceSummary = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (d = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = d ? { date: d } : {};
      const res = await AxiosAuthInstance.get("hr/attendance", { params });
      setData(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      setError("Failed to load attendance data");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load attendance data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchData(date);
  };

  const handleClearFilter = () => {
    setDate("");
    fetchData("");
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) => {
      const employeeName = item.employee?.employee_name?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const method = item.punch_method?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();
      
      return (
        employeeName.includes(searchTerm) ||
        status.includes(searchTerm) ||
        method.includes(searchTerm) ||
        item.attendance_date?.includes(searchTerm)
      );
    });
  }, [data, search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (!Array.isArray(data)) return { total: 0, present: 0, late: 0, absent: 0 };
    return {
      total: data.length,
      present: data.filter((d) => d.status === "present" && !d.is_late).length,
      late: data.filter((d) => d.is_late).length,
      absent: data.filter((d) => d.status === "absent").length,
    };
  }, [data]);

  const getStatusChip = (item) => {
    if (item.is_late) {
      return (
        <Chip
          icon={<FaExclamationCircle style={{ fontSize: 14 }} />}
          label="Late"
          size="small"
          sx={{
            bgcolor: "#fff3e0",
            color: "#ef6c00",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#ff9800" },
          }}
        />
      );
    }
    if (item.status === "present") {
      return (
        <Chip
          icon={<FaCheckCircle style={{ fontSize: 14 }} />}
          label="Present"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#4caf50" },
          }}
        />
      );
    }
    if (item.status === "absent") {
      return (
        <Chip
          icon={<FaTimesCircle style={{ fontSize: 14 }} />}
          label="Absent"
          size="small"
          sx={{
            bgcolor: "#ffebee",
            color: "#c62828",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#f44336" },
          }}
        />
      );
    }
    return (
      <Chip
        label={item.status || "N/A"}
        size="small"
        sx={{
          bgcolor: "#f5f5f5",
          color: "#757575",
          fontWeight: 600,
        }}
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    return timeStr;
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Attendance Summary" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Attendance Summary" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: { xs: "center", sm: "space-between" }, 
            alignItems: "center", 
            flexWrap: "wrap", 
            gap: 2,
            textAlign: { xs: "center", sm: "left" }
          }}
        >
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: "column", 
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" }
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserClock style={{ marginRight: 10, verticalAlign: "middle" }} />
              Attendance Summary
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              View and manage employee attendance records
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={() => fetchData(date)}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="total" elevation={0}>
            <CardContent>
              <FaUserClock size={32} color="#2196f3" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#1565c0">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="#1565c0" fontWeight={600}>
                Total Records
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="present" elevation={0}>
            <CardContent>
              <FaCheckCircle size={32} color="#4caf50" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#2e7d32">
                {stats.present}
              </Typography>
              <Typography variant="body2" color="#2e7d32" fontWeight={600}>
                Present
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="late" elevation={0}>
            <CardContent>
              <FaExclamationCircle size={32} color="#ff9800" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#ef6c00">
                {stats.late}
              </Typography>
              <Typography variant="body2" color="#ef6c00" fontWeight={600}>
                Late
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="absent" elevation={0}>
            <CardContent>
              <FaTimesCircle size={32} color="#f44336" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#c62828">
                {stats.absent}
              </Typography>
              <Typography variant="body2" color="#c62828" fontWeight={600}>
                Absent
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by name, status, method..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaCalendar style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FaFilter />}
              onClick={handleFilter}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              Filter
            </Button>
            {date && (
              <Button
                variant="outlined"
                onClick={handleClearFilter}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={80}>SL</StyledTableCell>
              <StyledTableCell>Employee</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Punch In</StyledTableCell>
              <StyledTableCell>Punch Out</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Method</StyledTableCell>
              <StyledTableCell>OT (min)</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton width={120} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaUser style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No attendance records found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || date ? "Try adjusting your filters" : "No data available"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const employeeName = row.employee?.employee_name || "N/A";
                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: getColorFromName(employeeName),
                            width: 40,
                            height: 40,
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(employeeName)}
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {employeeName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <FaCalendar style={{ fontSize: 14, color: "#757575" }} />
                        <Typography variant="body2">
                          {formatDate(row.attendance_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <FaClock style={{ fontSize: 14, color: "#4caf50" }} />
                        <Typography variant="body2" fontWeight={500}>
                          {formatTime(row.punch_in)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <FaClock style={{ fontSize: 14, color: "#f44336" }} />
                        <Typography variant="body2" fontWeight={500}>
                          {formatTime(row.punch_out)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(row)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.punch_method || "N/A"}
                        size="small"
                        sx={{
                          bgcolor: "#e3f2fd",
                          color: "#1976d2",
                          fontWeight: 500,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        color={row.overtime_minutes > 0 ? "success.main" : "text.secondary"}
                      >
                        {row.overtime_minutes || 0}
                      </Typography>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        )}
      </StyledTableContainer>
    </Box>
  );
};

export default AttendanceSummary;